import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye, EyeSlash, CaretDown, CaretLeft, CaretRight, Bell, Info, Drop, ArrowUpRight, ArrowDown,
  CheckCircle, WarningCircle, Clock, Minus, ArrowRight, PencilSimple, FloppyDisk,
} from "@phosphor-icons/react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
} from "recharts";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { api, ASSET_TYPE_LABELS, assetDelinquent } from "@/lib/api";
import { toast } from "sonner";

const CLASS_META = {
  producing: { label: "Producing", token: "--ploy-brass" },
  royalty:   { label: "Royalty",   token: "--ploy-pos" },
  held:      { label: "Held",      token: "--ploy-neutral" },
};
const CLASS_ORDER = ["producing", "royalty", "held"];

const STATUS_STYLES = {
  success:     { bg: "bg-[hsl(var(--ploy-pos-soft))]",     text: "text-[hsl(var(--ploy-pos))]",     icon: CheckCircle },
  warning:     { bg: "bg-[hsl(var(--ploy-attn-soft))]",    text: "text-[hsl(var(--ploy-attn))]",    icon: WarningCircle },
  destructive: { bg: "bg-[hsl(var(--ploy-rust-soft))]",    text: "text-[hsl(var(--ploy-rust))]",    icon: Clock },
  muted:       { bg: "bg-[hsl(var(--ploy-neutral-soft))]", text: "text-[hsl(var(--ploy-neutral))]", icon: Minus },
};
const StatusPill = ({ status, testid }) => {
  const s = STATUS_STYLES[status.tone] || STATUS_STYLES.muted;
  const Icon = s.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <Icon size={11} weight="fill" /> {status.label}
    </span>
  );
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const distMonth = (d) => {
  const cd = (d?.check_date || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(cd)) return cd;
  const m = /^(\d{2})\/(\d{4})$/.exec(d?.period_max || "");
  return m ? `${m[2]}-${m[1]}` : "";
};
const distVal = (d) => Number(d?.owner_net) || Number(d?.paid_total) || 0;

export default function MineralsSilo() {
  const navigate = useNavigate();
  const { fmtAsset } = useDemo();
  const { assets, entities, liabilities, ownerLabels, ownerLabel, isOperator,
          hid, refreshAssets, assetProblemIds, liabilityDueIds } = useHousehold();

  const [ownerFilter, setOwnerFilter] = useState("both");
  const [entityFilter, setEntityFilter] = useState("all");
  const [privacy, setPrivacy] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [annualizeTo, setAnnualizeTo] = useState("month-end");   // visual only
  const [activeKpi, setActiveKpi] = useState("invested");        // invested | cashflow | return | attention

  const minerals = useMemo(() => {
    let list = (assets || []).filter((a) => a.asset_type === "mineral_royalty");
    if (ownerFilter !== "both") list = list.filter((a) => a.owner_party === ownerFilter || a.owner_party === "joint");
    if (entityFilter !== "all") list = list.filter((a) => a.entity_id === entityFilter);
    return list;
  }, [assets, ownerFilter, entityFilter]);

  const entityName = (id) => entities?.find((e) => e.id === id)?.name || "—";

  const invested  = (a) => Number(a.meta?.total_invested || 0);
  const ogClass   = (a) => (CLASS_META[a.meta?.og_class] ? a.meta.og_class : "producing");
  const paidBack  = (a) => (Array.isArray(a.meta?.distributions) ? a.meta.distributions : []).reduce((s, d) => s + distVal(d), 0);
  const pctBack   = (a) => { const inv = invested(a); return inv > 0 ? (paidBack(a) / inv) * 100 : 0; };
  const breakEven = (a) => Math.max(0, invested(a) - paidBack(a));

  const totalInvested  = minerals.reduce((s, a) => s + invested(a), 0);
  const totalBack      = minerals.reduce((s, a) => s + paidBack(a), 0);
  const totalBreakEven = Math.max(0, totalInvested - totalBack);
  const pctBackAll     = totalInvested > 0 ? (totalBack / totalInvested) * 100 : 0;

  // monthly buckets
  const monthBuckets = useMemo(() => {
    const b = {};
    minerals.forEach((a) => {
      const dists = Array.isArray(a.meta?.distributions) ? a.meta.distributions : [];
      const seen = new Set();
      dists.forEach((d) => {
        const mk = distMonth(d); if (!mk) return;
        const dedupe = `${a.id}|${mk}|${d.check_number || ""}|${distVal(d)}`;
        if (seen.has(dedupe)) return; seen.add(dedupe);
        b[mk] = (b[mk] || 0) + distVal(d);
      });
    });
    return b;
  }, [minerals]);

  const monthKeys = useMemo(() => Object.keys(monthBuckets).filter((k) => monthBuckets[k] > 0).sort(), [monthBuckets]);
  const [monthIdx, setMonthIdx] = useState(null);
  const activeIdx = monthIdx == null ? monthKeys.length - 1 : Math.min(monthIdx, monthKeys.length - 1);
  const activeMonthKey = monthKeys[activeIdx];
  const monthValue = activeMonthKey ? monthBuckets[activeMonthKey] : 0;
  const prevMonthValue = activeIdx > 0 ? monthBuckets[monthKeys[activeIdx - 1]] : 0;
  const monthDelta = monthValue - prevMonthValue;
  const monthLabel = activeMonthKey
    ? `${MONTHS[parseInt(activeMonthKey.slice(5, 7), 10) - 1]} ${activeMonthKey.slice(0, 4)}`.toUpperCase()
    : "—";

  // trailing 12-month series (calendar-aligned to current month)
  const trailing = useMemo(() => {
    const now = new Date();
    const arr = [];
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mk = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const py = new Date(dt.getFullYear() - 1, dt.getMonth(), 1);
      const pyk = `${py.getFullYear()}-${String(py.getMonth() + 1).padStart(2, "0")}`;
      arr.push({ month: MONTHS[dt.getMonth()], value: monthBuckets[mk] || 0, prior: monthBuckets[pyk] || 0, isCurrent: i === 0 });
    }
    return arr;
  }, [monthBuckets]);
  const trailingAvg = (n) => { const s = trailing.slice(12 - n); return s.length ? s.reduce((x, p) => x + p.value, 0) / s.length : 0; };
  const t3 = trailingAvg(3), t6 = trailingAvg(6), t12 = trailingAvg(12);
  const hasPrior = trailing.some((p) => p.prior > 0);
  // Headline reflects the most recent PAYING month (current calendar month is often $0).
  const payMomPct = prevMonthValue > 0 ? ((monthValue - prevMonthValue) / prevMonthValue) * 100 : 0;

  const attentionList = minerals.filter((a) =>
    a.review_status === "needs_review" || assetDelinquent(a) || a.lease_alert || assetProblemIds?.has(a.id)
  );

  const statusFor = (a) => {
    const isLate = (liabilities || []).some((l) => l.asset_id === a.id && liabilityDueIds?.has(l.id));
    if (a.review_status === "needs_review" || assetProblemIds?.has(a.id) || isLate) return { tone: "warning", label: "Attention" };
    if (ogClass(a) === "held") return { tone: "muted", label: "Held" };
    if (paidBack(a) > 0) return { tone: "success", label: "Producing" };
    return { tone: "muted", label: "Idle" };
  };

  const donutData = [
    { name: "Paid back", value: Math.max(0, totalBack) },
    { name: "To break even", value: Math.max(0, totalBreakEven) },
  ];
  const groups = CLASS_ORDER.map((cls) => {
    const items = minerals.filter((a) => ogClass(a) === cls);
    const inv = items.reduce((s, a) => s + invested(a), 0);
    return { cls, ...CLASS_META[cls], items, inv, pct: totalInvested > 0 ? (inv / totalInvested) * 100 : 0 };
  }).filter((g) => g.items.length > 0);

  const fmt = (n) => (privacy ? "•••••" : fmtAsset(n));
  const fmtK = (n) => {
    if (privacy) return "•••";
    const abs = Math.abs(Number(n) || 0);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return fmtAsset(n);
  };

  const ctx = { fmt, fmtK, privacy, statusFor, ownerLabel, entityName, invested, ogClass, paidBack, pctBack, breakEven, hid, refreshAssets, isOperator };

  return (
    <div className="space-y-7 animate-fade-up" data-testid="minerals-silo">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap pb-5 border-b border-border">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[hsl(var(--ploy-brass-soft))] text-[hsl(var(--accent))] flex items-center justify-center shrink-0" data-testid="silo-icon-box">
            <Drop size={24} weight="regular" />
          </div>
          <div>
            <p className="eyebrow">Portfolio · Oil &amp; Gas</p>
            <h1 className="page-title text-[40px] sm:text-[48px] mt-1 leading-none" data-testid="page-title">Oil &amp; Gas</h1>
            <p className="text-sm text-muted-foreground mt-2.5" data-testid="silo-holdings-count">
              {minerals.length} mineral {minerals.length === 1 ? "interest" : "interests"} across producing royalties, royalties, and held tracts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full bg-[hsl(var(--ploy-input-well))] p-0.5">
            {[{ k: "both", l: "Both" }, { k: "A", l: ownerLabels?.A || "A" }, { k: "B", l: ownerLabels?.B || "B" }].map((o) => (
              <button key={o.k} data-testid={`owner-filter-${o.k}`} onClick={() => setOwnerFilter(o.k)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${ownerFilter === o.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}>
                {o.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <select data-testid="entity-filter" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 text-xs font-semibold rounded-full bg-[hsl(var(--ploy-input-well))] hover:bg-[hsl(var(--ploy-border))] text-foreground border border-transparent focus:outline-none cursor-pointer">
              <option value="all">All entities</option>
              {(entities || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <CaretDown size={11} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
          </div>
          <button data-testid="privacy-toggle" onClick={() => setPrivacy((p) => !p)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              privacy ? "bg-foreground text-background border-foreground" : "bg-transparent border-[hsl(var(--ploy-input-border))] text-foreground/80 hover:bg-[hsl(var(--ploy-input-well))]"
            }`}>
            {privacy ? <EyeSlash size={12} /> : <Eye size={12} />} Privacy
          </button>
        </div>
      </div>

      {/* KPI tab strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total invested */}
        <KpiCard kpi="invested" active={activeKpi} onSelect={setActiveKpi} testid="kpi-total-invested" label="Total Invested">
          <p className="hero-metric text-3xl mt-2 text-foreground">{fmtK(totalInvested)}</p>
          <p className="text-[12px] mt-1.5">
            <span className="font-semibold text-[hsl(var(--ploy-pos))]">{fmtK(totalBack)} back</span>
            <span className="text-muted-foreground"> · {pctBackAll.toFixed(1)}%</span>
          </p>
          <p className="text-[11px] text-[hsl(var(--ploy-rust))]">{fmtK(totalBreakEven)} to break even</p>
          <div className="mt-3 h-1.5 rounded-full bg-[hsl(var(--ploy-input-well))] overflow-hidden">
            <div className="h-full rounded-full bg-[hsl(var(--accent))]" style={{ width: `${Math.min(100, pctBackAll)}%` }} />
          </div>
        </KpiCard>

        {/* Monthly distributions */}
        <KpiCard kpi="cashflow" active={activeKpi} onSelect={setActiveKpi} testid="kpi-monthly-distributions" label="Monthly Distributions">
          <div className="flex items-baseline gap-2 mt-2">
            <p className="hero-metric text-3xl text-foreground">{fmt(monthValue)}</p>
            {monthKeys.length > 1 && monthDelta !== 0 && (
              <span className={`text-[12px] font-semibold inline-flex items-center gap-0.5 ${monthDelta >= 0 ? "text-[hsl(var(--ploy-pos))]" : "text-[hsl(var(--ploy-rust))]"}`}>
                {monthDelta >= 0 ? <ArrowUpRight size={11} /> : <ArrowDown size={11} />}{fmt(Math.abs(monthDelta))}
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">Income less carrying cost</p>
          <div className="mt-3 flex items-center justify-center gap-3">
            <button data-testid="month-prev" disabled={activeIdx <= 0}
              onClick={(e) => { e.stopPropagation(); setActiveKpi("cashflow"); setMonthIdx(Math.max(0, activeIdx - 1)); }}
              className="p-1 rounded-md hover:bg-[hsl(var(--ploy-input-well))] disabled:opacity-30 disabled:cursor-not-allowed">
              <CaretLeft size={13} />
            </button>
            <span className="text-[11px] font-semibold tracking-[0.12em] text-foreground/80 min-w-[78px] text-center px-2 py-0.5 rounded-full border border-[hsl(var(--ploy-input-border))]" data-testid="month-label">{monthLabel}</span>
            <button data-testid="month-next" disabled={activeIdx >= monthKeys.length - 1}
              onClick={(e) => { e.stopPropagation(); setActiveKpi("cashflow"); setMonthIdx(Math.min(monthKeys.length - 1, activeIdx + 1)); }}
              className="p-1 rounded-md hover:bg-[hsl(var(--ploy-input-well))] disabled:opacity-30 disabled:cursor-not-allowed">
              <CaretRight size={13} />
            </button>
          </div>
        </KpiCard>

        {/* Lifetime avg annual return — visual only */}
        <KpiCard kpi="return" active={activeKpi} onSelect={setActiveKpi} testid="kpi-annual-return" label="Lifetime Avg. Annual Return">
          <div className="flex items-center gap-2 mt-2">
            <p className="hero-metric text-3xl text-muted-foreground/50">—</p>
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[hsl(var(--ploy-neutral-soft))] text-[hsl(var(--ploy-neutral))]">Soon</span>
          </div>
          <div className="mt-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">Annualize To</p>
            <div className="inline-flex rounded-full bg-[hsl(var(--ploy-input-well))] p-0.5">
              {[{ k: "today", l: "Today" }, { k: "month-end", l: "Month-End" }].map((o) => (
                <button key={o.k} data-testid={`annualize-${o.k}`}
                  onClick={(e) => { e.stopPropagation(); setActiveKpi("return"); setAnnualizeTo(o.k); }}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${annualizeTo === o.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </KpiCard>

        {/* Needs attention */}
        <KpiCard kpi="attention" active={activeKpi} onSelect={setActiveKpi} testid="kpi-needs-attention" label="Needs Attention">
          <p className={`hero-metric text-3xl mt-2 ${attentionList.length > 0 ? "text-[hsl(var(--ploy-attn))]" : "text-foreground"}`}>{attentionList.length}</p>
          {attentionList.length > 0 ? (
            <p className="text-[11px] text-[hsl(var(--ploy-attn))] mt-1.5 flex items-center gap-1"><Bell size={11} weight="fill" /> Action items flagged below</p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1.5">All clear</p>
          )}
        </KpiCard>
      </div>

      {/* Contextual detail panel — content driven by the selected KPI */}
      <div className="bg-card rounded-2xl border-2 border-[hsl(var(--accent)/0.55)] overflow-hidden" data-testid="minerals-detail-panel">
        {activeKpi === "invested" && (
          <InvestedPanel minerals={minerals} groups={groups} donutData={donutData}
            totalInvested={totalInvested} totalBack={totalBack} totalBreakEven={totalBreakEven} pctBackAll={pctBackAll}
            ctx={ctx} onPick={setSelectedAsset} />
        )}
        {activeKpi === "cashflow" && (
          <CashflowPanel trailing={trailing} latestVal={monthValue} momPct={payMomPct} hasPrior={hasPrior}
            t3={t3} t6={t6} t12={t12} fmt={fmt} privacy={privacy} />
        )}
        {activeKpi === "return" && <ReturnPanel annualizeTo={annualizeTo} />}
        {activeKpi === "attention" && (
          <AttentionPanel attentionList={attentionList} statusFor={statusFor} entityName={entityName} fmtK={fmtK} onPick={setSelectedAsset} />
        )}
      </div>

      <MineralPeekDrawer asset={selectedAsset} onClose={() => setSelectedAsset(null)}
        onOpenFull={(a) => { setSelectedAsset(null); navigate(`/assets/${a.id}`); }} ctx={{ ...ctx, setSelectedAsset }} />
    </div>
  );
}

// ── KPI tab card with selected ring + downward caret connector ──
const KpiCard = ({ kpi, active, onSelect, testid, label, children }) => {
  const selected = active === kpi;
  return (
    <div data-testid={testid} role="button" tabIndex={0}
      onClick={() => onSelect(kpi)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(kpi); } }}
      className={`relative bg-card rounded-2xl px-5 py-5 border cursor-pointer transition-all ${
        selected ? "border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))] shadow-[0_4px_18px_hsl(var(--ploy-ink)/0.08)]"
                 : "border-border hover:border-[hsl(var(--ploy-input-border))]"
      }`}>
      <p className="eyebrow flex items-center justify-between gap-1">{label}
        <CaretDown size={11} className={`transition-transform ${selected ? "rotate-180 text-[hsl(var(--accent))]" : "opacity-40"}`} />
      </p>
      {children}
      {selected && (
        <span data-testid={`${testid}-caret`}
          className="absolute -bottom-[10px] left-1/2 -translate-x-1/2 h-4 w-4 rotate-45 bg-card border-b border-r border-[hsl(var(--accent))] z-10" />
      )}
    </div>
  );
};

// ── Panel: Total Invested (donut + grouped holdings) ──
const InvestedPanel = ({ minerals, groups, donutData, totalInvested, totalBack, totalBreakEven, pctBackAll, ctx, onPick }) => {
  const { fmtK, invested, paidBack, pctBack, breakEven, entityName } = ctx;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr]">
        <div className="flex flex-col items-center justify-center gap-4 p-6 border-b lg:border-b-0 lg:border-r border-border" data-testid="invested-donut">
          <div className="relative h-[180px] w-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donutData} dataKey="value" innerRadius={62} outerRadius={84} startAngle={90} endAngle={-270} stroke="none" paddingAngle={donutData.every((d) => d.value > 0) ? 2 : 0}>
                  <Cell fill="hsl(var(--ploy-pos))" />
                  <Cell fill="hsl(var(--ploy-input-well))" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Invested</p>
              <p className="hero-metric text-2xl text-foreground leading-tight">{fmtK(totalInvested)}</p>
              <p className="text-[11px] text-muted-foreground">{pctBackAll.toFixed(0)}% paid back</p>
            </div>
          </div>
          <div className="space-y-1.5 w-full px-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="flex items-center gap-1.5 text-foreground/80"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--ploy-pos))]" /> Paid back</span>
              <span className="tabular-nums font-semibold">{fmtK(totalBack)}</span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="flex items-center gap-1.5 text-foreground/80"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--ploy-input-well))]" /> To break even</span>
              <span className="tabular-nums font-semibold">{fmtK(totalBreakEven)}</span>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6" data-testid="minerals-groups">
          {minerals.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-serif text-xl">No mineral interests yet</p>
              <p className="text-sm text-muted-foreground mt-1">Upload a statement on the Ingest page to begin tracking it here.</p>
            </div>
          ) : (
            <div className="space-y-7">
              {groups.map((g) => (
                <div key={g.cls} data-testid={`group-${g.cls}`}>
                  <div className="flex items-center justify-between pb-2.5 mb-1 border-b border-border">
                    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: `hsl(var(${g.token}))` }} />
                      <span style={{ color: `hsl(var(${g.token}))` }}>{g.label}</span>
                    </span>
                    <span className="text-[12px] tabular-nums text-muted-foreground">{fmtK(g.inv)} · {g.pct.toFixed(1)}%</span>
                  </div>
                  <div className="divide-y divide-border/70">
                    {g.items.map((a) => {
                      const inv = invested(a); const pb = pctBack(a); const be = breakEven(a);
                      return (
                        <button key={a.id} data-testid={`holding-row-${a.id}`} onClick={() => onPick(a)}
                          className="w-full text-left py-3 group hover:bg-[hsl(var(--ploy-row-hover))] -mx-2 px-2 rounded-md transition-colors">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <span className="font-semibold text-foreground">{a.name}</span>
                              <span className="text-[12px] text-muted-foreground ml-2">{entityName(a.entity_id)}</span>
                            </div>
                            <div className="text-right shrink-0 tabular-nums">
                              <span className="font-semibold text-foreground">{fmtK(inv)}</span>
                              <span className="text-[12px] text-muted-foreground ml-1.5">{(totalInvested > 0 ? (inv / totalInvested) * 100 : 0).toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--ploy-input-well))] overflow-hidden">
                            <div className="h-full rounded-full bg-[hsl(var(--ploy-pos))]" style={{ width: `${Math.min(100, pb)}%` }} />
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">
                            {pb.toFixed(1)}% paid back · {fmtK(paidBack(a))} of {fmtK(inv)} · {fmtK(be)} to break even
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="px-5 py-2.5 text-[11px] text-muted-foreground bg-muted border-t border-border flex items-center gap-1.5">
        <Info size={11} /> Invested is editable per package · paid-back is the lifetime sum of filed distributions.
      </div>
    </>
  );
};

// ── Panel: Monthly Distributions (trend chart + trailing averages) ──
const CashflowPanel = ({ trailing, latestVal, momPct, hasPrior, t3, t6, t12, fmt, privacy }) => {
  const avgRow = (label, val, base, testid) => {
    const delta = base > 0 ? ((val - base) / base) * 100 : 0;
    const up = delta >= 0;
    return (
      <div className="flex items-center justify-between py-3.5 border-b border-border last:border-0" data-testid={testid}>
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-3">
          <span className="hero-metric text-xl text-foreground tabular-nums">{fmt(val)}</span>
          {base > 0 ? (
            <span className={`text-[12px] font-semibold inline-flex items-center gap-0.5 tabular-nums ${up ? "text-[hsl(var(--ploy-pos))]" : "text-[hsl(var(--ploy-attn))]"}`}>
              {up ? <ArrowUpRight size={11} /> : <ArrowDown size={11} />}{Math.abs(delta).toFixed(0)}%
            </span>
          ) : <span className="text-muted-foreground">—</span>}
        </div>
      </div>
    );
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-5 p-5 sm:p-6" data-testid="cashflow-panel">
      <div>
        <div className="flex items-center justify-between">
          <p className="eyebrow">Net Distributions · Trailing 12 mo</p>
          {momPct !== 0 && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${momPct >= 0 ? "text-[hsl(var(--ploy-pos))]" : "text-[hsl(var(--ploy-rust))]"}`}>
              <ArrowUpRight size={11} /> {momPct >= 0 ? "+" : ""}{momPct.toFixed(1)}% MoM
            </span>
          )}
        </div>
        <p className="hero-metric text-2xl mt-1 text-foreground">{fmt(latestVal)}</p>
        <div className="h-44 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trailing} margin={{ top: 8, right: 4, left: -10, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip cursor={{ fill: "hsl(var(--ploy-row-hover))" }}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                formatter={(v, n) => [privacy ? "•••" : fmt(v), n === "prior" ? "Prior yr" : "Net"]} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={34} stroke="none">
                {trailing.map((p, i) => <Cell key={i} fill={p.isCurrent ? "hsl(var(--accent))" : "hsl(var(--ploy-brass)/0.45)"} />)}
              </Bar>
              {hasPrior && <Line type="monotone" dataKey="prior" stroke="hsl(var(--ploy-ink-4))" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {hasPrior && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5"><span className="inline-block w-5 border-t border-dashed border-[hsl(var(--ploy-ink-4))]" /> Same month, prior year</p>}
      </div>
      <div className="bg-background rounded-xl border border-border p-5">
        <p className="eyebrow mb-1">Trailing Averages</p>
        {avgRow("Trailing 3 mo", t3, t12, "avg-3mo")}
        {avgRow("Trailing 6 mo", t6, t12, "avg-6mo")}
        <div className="flex items-center justify-between py-3.5" data-testid="avg-12mo">
          <span className="text-sm text-muted-foreground">Trailing 12 mo</span>
          <span className="hero-metric text-xl text-foreground tabular-nums">{fmt(t12)}</span>
        </div>
      </div>
    </div>
  );
};

// ── Panel: Lifetime Avg Annual Return (visual placeholder) ──
const ReturnPanel = ({ annualizeTo }) => (
  <div className="p-10 text-center" data-testid="return-panel">
    <p className="eyebrow">Lifetime Avg. Annual Return</p>
    <p className="hero-metric text-5xl text-muted-foreground/40 mt-3">—</p>
    <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
      Annualized return math is coming soon. Once cost-basis dates and a full distribution history are in,
      this will compute IRR / cash-on-cash annualized to <span className="font-semibold text-foreground">{annualizeTo === "today" ? "today" : "month-end"}</span>.
    </p>
  </div>
);

// ── Panel: Needs Attention (flagged holdings list) ──
const AttentionPanel = ({ attentionList, statusFor, entityName, fmtK, onPick }) => (
  <div className="p-5 sm:p-6" data-testid="attention-panel">
    <p className="eyebrow mb-3">Action Items</p>
    {attentionList.length === 0 ? (
      <div className="py-10 text-center">
        <CheckCircle size={26} weight="fill" className="mx-auto text-[hsl(var(--ploy-pos))]" />
        <p className="font-serif text-lg mt-2">Everything's in order</p>
        <p className="text-sm text-muted-foreground mt-1">No mineral packages need attention right now.</p>
      </div>
    ) : (
      <div className="divide-y divide-border">
        {attentionList.map((a) => (
          <button key={a.id} data-testid={`attention-row-${a.id}`} onClick={() => onPick(a)}
            className="w-full flex items-center justify-between gap-3 py-3.5 text-left hover:bg-[hsl(var(--ploy-row-hover))] -mx-2 px-2 rounded-md transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <StatusPill status={statusFor(a)} testid={`attention-status-${a.id}`} />
              <div className="min-w-0">
                <p className="font-semibold text-foreground truncate">{a.name}</p>
                <p className="text-[12px] text-muted-foreground truncate">{entityName(a.entity_id)}</p>
              </div>
            </div>
            <CaretRight size={14} className="text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const MineralPeekDrawer = ({ asset, onClose, onOpenFull, ctx }) => {
  const { fmtK, statusFor, ownerLabel, entityName, invested, ogClass, paidBack, pctBack, breakEven, hid, refreshAssets, isOperator } = ctx;
  const [editing, setEditing] = useState(false);
  const [invVal, setInvVal] = useState("");
  const [clsVal, setClsVal] = useState("producing");
  const [saving, setSaving] = useState(false);

  if (!asset) return null;
  const status = statusFor(asset);
  const inv = invested(asset);
  const pb = pctBack(asset);

  const startEdit = () => { setInvVal(String(inv || "")); setClsVal(ogClass(asset)); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/assets/${asset.id}/mineral-meta?household_id=${hid}`, {
        total_invested: invVal === "" ? 0 : Number(invVal), og_class: clsVal,
      });
      await refreshAssets();
      toast.success("Package updated");
      setEditing(false); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={!!asset} onOpenChange={(o) => { if (!o) { setEditing(false); onClose(); } }}>
      <SheetContent side="right" data-testid="mineral-peek-drawer"
        className="w-[94vw] sm:max-w-[460px] p-0 flex flex-col bg-background border-l border-border">
        <SheetTitle className="sr-only">{asset.name} · mineral package detail</SheetTitle>
        <SheetDescription className="sr-only">Return of capital and editable invested amount for {asset.name}.</SheetDescription>
        <div className="px-6 pt-6 pb-5 border-b border-border">
          <StatusPill status={status} testid="peek-status-pill" />
          <h2 className="font-serif text-3xl font-medium tracking-tight mt-3 leading-tight pr-8" data-testid="peek-asset-name">{asset.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{CLASS_META[ogClass(asset)].label} · {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="rounded-xl border border-border p-4" data-testid="peek-capital">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Return of Capital</p>
              {isOperator && !editing && (
                <button data-testid="peek-edit-button" onClick={startEdit} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--accent))] hover:underline">
                  <PencilSimple size={12} /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground">Total invested ($)</label>
                  <input data-testid="edit-invested-input" type="number" value={invVal} onChange={(e) => setInvVal(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-[hsl(var(--ploy-input-border))] bg-[hsl(var(--ploy-input-well))] px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground">Classification</label>
                  <select data-testid="edit-class-select" value={clsVal} onChange={(e) => setClsVal(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-[hsl(var(--ploy-input-border))] bg-[hsl(var(--ploy-input-well))] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]">
                    {CLASS_ORDER.map((c) => <option key={c} value={c}>{CLASS_META[c].label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid="edit-save-button" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                    <FloppyDisk size={14} /> {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg text-sm font-semibold text-foreground/70 hover:bg-[hsl(var(--ploy-input-well))]">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p className="hero-metric text-2xl mt-2 text-foreground">{fmtK(inv)} <span className="text-sm font-normal text-muted-foreground">invested</span></p>
                <div className="mt-3 h-1.5 rounded-full bg-[hsl(var(--ploy-input-well))] overflow-hidden">
                  <div className="h-full rounded-full bg-[hsl(var(--ploy-pos))]" style={{ width: `${Math.min(100, pb)}%` }} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 tabular-nums">
                  {pb.toFixed(1)}% paid back · {fmtK(paidBack(asset))} of {fmtK(inv)} · {fmtK(breakEven(asset))} to break even
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-4" data-testid="peek-facts-grid">
            <Fact label="Owner" value={ownerLabel(asset.owner_party)} testid="peek-fact-owner" />
            <Fact label="Entity" value={entityName(asset.entity_id)} testid="peek-fact-entity" />
            <Fact label="Operator" value={asset.meta?.operator || "—"} testid="peek-fact-operator" />
            <Fact label="Wells" value={asset.meta?.well_count ?? "—"} testid="peek-fact-wells" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-background">
          <button data-testid="peek-open-full-button" onClick={() => onOpenFull(asset)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[0.625rem] bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity">
            Open full detail <ArrowRight size={15} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Fact = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    <p className="text-sm text-foreground mt-1 tabular-nums">{value ?? "—"}</p>
  </div>
);
